import {
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	defaultRemoteConfigKey,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PermissionName } from "../../../shared/types/permission";
import { ContactFilterContextMenu } from "../contactFilter/ContactFilterContextMenu";
import { contactDatabaseSettingsService } from "../shared/api/contactDatabaseSettingsService";
import { contactFilterService } from "../shared/api/contactFilterService";
import { useContactAttributeOptions } from "../shared/hooks/useContactAttributeOptions";
import { ContactFilterIndexResource } from "../shared/types";
import { ContactFilterCreateDrawer, ContactFilterUpdateDrawer } from "./ContactFilterDrawer";
import { mapFormValuesToRequest } from "./mappers";

export const ContactFilterPage: FC = () => {
	const { contactAttributeOptions } = useContactAttributeOptions();
	const { t } = useTranslation();

	const crudOperations = useLaravelCrudEntity(
		contactFilterService,
		{
			entityName: t("Contact filter"),
			messages: {
				create: t("Contact filter was saved."),
				update: t("Contact filter was saved."),
				delete: t("Contact filter was deleted."),
			},
		},
		[defaultRemoteConfigKey, contactDatabaseSettingsService.keys.getSettings, contactFilterService.forSidebarKey],
	);
	const canCreate = useHasPermission<PermissionName>(["contactFilter.create"]);
	const canUpdate = useHasPermission<PermissionName>(["contactFilter.update"]);

	const columns: IDrawerDataListColumn<ContactFilterIndexResource>[] = useMemo(
		() => [
			{ heading: t("Name"), valueKey: "name", type: "value" },
			{
				heading: t("Type"),
				type: "value",
				valueKey: "type",
				render: (filter) => t("contactFilterType", { context: filter.type }),
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: (contactFilter, openDrawer) => (
					<ContactFilterContextMenu
						filterId={contactFilter.id}
						handleEdit={openDrawer}
						handleDelete={() => crudOperations.deleteEntity(contactFilter.id)}
					/>
				),
			},
		],
		[t, crudOperations],
	);

	return (
		<PaginatedDrawerAutoDataList
			canCreate={canCreate}
			canUpdate={canUpdate}
			columns={columns}
			translations={{ empty: t("No contact filters available."), title: t("Contact filters") }}
			listFetchConfig={laravelPaginatedListFetchConfig(contactFilterService.listKey, contactFilterService.index)}
			renderCreateDrawer={(props) => (
				<ContactFilterCreateDrawer
					{...props}
					onSubmit={(formValues) =>
						crudOperations.createEntity(mapFormValuesToRequest(formValues, contactAttributeOptions))
					}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<ContactFilterUpdateDrawer
					{...drawerProps}
					onSubmit={(formValues) =>
						crudOperations.updateEntity(drawerProps.id, mapFormValuesToRequest(formValues, contactAttributeOptions))
					}
				/>
			)}
			initialSearch={{ search: "", searchFields: [{ label: t("Name"), name: "name" }] }}
		/>
	);
};
